import { useQueryClient } from '@tanstack/react-query'
import { memo } from 'react'
import { Chip } from '@/components/commons/chip'
import { GalleryImageItem } from '@/components/commons/gallery-image-item'
import { ButtonLegacy } from '@/components/ui/button-legacy'
import { Expand01Icon, Trash01Icon } from '@/components/ui/icons'
import { Spinner } from '@/components/ui/spinner'
import { OpsVettingGalleryItemsContainer } from '@/features/ops-vetting/components/ops-vetting-atoms'
import { QUERY_KEYS } from '@/query-client'
import { useTranslation } from '@/translations/i18n.tsx'
import { useDeleteServicePhotos } from '../query/use-delete-service-photos'
import type { ServiceGalleryImage } from '../types'

export const ServiceVettingCriteriaUploadedItem = memo(
	({
		image,
		galleryId,
		serviceId,
		hideAction,
		onClickExpand
	}: {
		image: ServiceGalleryImage
		galleryId: number
		serviceId: number
		hideAction: boolean
		onClickExpand?: (image: ServiceGalleryImage) => void
	}) => {
		const { t } = useTranslation()
		const queryClient = useQueryClient()

		const deletePhoto = useDeleteServicePhotos(
			{ galleryId, serviceId },
			{
				onSuccess: () => {
					queryClient.invalidateQueries({
						queryKey: [QUERY_KEYS.SERVICE_GALLERY_IMAGES]
					})
					queryClient.invalidateQueries({
						queryKey: [QUERY_KEYS.SERVICE_VETTING, serviceId]
					})
				}
			}
		)

		const onClickDeletePhoto = (e: React.MouseEvent<HTMLButtonElement>) => {
			e.stopPropagation()
			deletePhoto.mutate({ mediaId: image.id })
		}

		return (
			<OpsVettingGalleryItemsContainer>
				<GalleryImageItem
					bottomLeftSlot={
						<Chip
							label={t(
								'vetting:criteria.service.gallery.internal_upload'
							)}
							variant={'neutral'}
						/>
					}
					bottomRightSlot={
						!hideAction && (
							<ButtonLegacy
								className='show-on-hover'
								disabled={deletePhoto.isPending}
								onClick={() => onClickExpand?.(image)}
								shape='square'
								size='sm'
								type='button'
								variant='secondary'
							>
								<Expand01Icon />
							</ButtonLegacy>
						)
					}
					src={image.thumbnails?.xs || image.url}
					topRightSlot={
						<ButtonLegacy
							disabled={deletePhoto.isPending || hideAction}
							onClick={onClickDeletePhoto}
							shape='square'
							type='button'
							variant={'secondary'}
						>
							{deletePhoto.isPending ? (
								<Spinner />
							) : (
								<Trash01Icon />
							)}
						</ButtonLegacy>
					}
				/>
			</OpsVettingGalleryItemsContainer>
		)
	}
)
